<template>
  <v-main>
    <v-container fluid style="height: 100%" class="flex flex-col">
      <div class="flex items-center pl-3">
        <div class="text-xl">{{ t("accounts.title") }}</div>
        <v-spacer></v-spacer>
        <v-btn-toggle
          v-model="activeAccountsFilter"
          mandatory
          style="height: 40px"
          density="compact"
          class="mr-3"
          variant="outlined"
          color="primary"
          divided
        >
          <v-btn value="actived"> {{ t("accounts.active") }} </v-btn>

          <v-btn value="archived"> {{ t("accounts.archived") }} </v-btn>
        </v-btn-toggle>
        <v-btn-toggle
          v-if="!isExternalApprover"
          v-model="ownerFilter"
          :disabled="user.isKind('admin')"
          mandatory
          style="height: 40px"
          density="compact"
          class="mr-3"
          variant="outlined"
          color="primary"
          divided
        >
          <v-btn value="mine"> {{ t("accounts.myAccounts") }} </v-btn>
          <v-btn value="all"> {{ t("accounts.allAccounts") }} </v-btn>
        </v-btn-toggle>
        <v-text-field
          ref="searchInputRef"
          v-model="searchQuery"
          color="primary"
          :placeholder="t('common.search')"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          class="mr-3"
          variant="outlined"
          style="max-width: 300px"
        >
          <template #append-inner>
            <span :class="['border-theme rounded text-disabled text-xs px-1']">
              <span>{{ shortcutLabel }}</span>
            </span>
          </template>
        </v-text-field>
        <v-btn
          v-if="user.hasRole(supervisorRoles, 'supervisorOrg')"
          height="40px"
          color="primary"
          variant="outlined"
          append-icon="mdi-plus"
          data-testid="accounts-add-button"
          @click="showAddAccountDialog = true"
          >{{ t("common.add") }}</v-btn
        >
      </div>

      <div
        v-if="organizationInvitations.length"
        class="mt-6"
        data-testid="accounts-organization-invitations"
      >
        <div class="font-bold pl-3">{{ t("accounts.organizationInvitations.title") }}</div>
        <v-table density="compact" class="mt-2">
          <tbody>
            <tr
              v-for="organizationInvitation in organizationInvitations"
              :key="organizationInvitation.id"
            >
              <td>
                {{ organizationInvitation.companyName }}
                <span class="text-xs text-medium-emphasis ml-2">{{
                  organizationInvitation.siren
                }}</span>
              </td>
              <td width="160px" class="text-medium-emphasis">
                {{ organizationInvitation.createdAt.toDate().toLocaleDateString(locale) }}
              </td>
              <td width="100px">
                <v-chip
                  size="small"
                  variant="tonal"
                  :color="organizationInvitation.status === 'refused' ? 'error' : 'primary'"
                  >{{
                    t(`accounts.organizationInvitations.${organizationInvitation.status}`)
                  }}</v-chip
                >
              </td>
              <td width="200px" class="text-right">
                <v-btn
                  v-if="user.hasRole(supervisorRoles, 'supervisorOrg')"
                  v-tooltip="
                    organizationInvitation.status === 'pending'
                      ? t('accounts.organizationInvitations.cancel')
                      : t('accounts.organizationInvitations.remove')
                  "
                  :aria-label="
                    organizationInvitation.status === 'pending'
                      ? t('accounts.organizationInvitations.cancel')
                      : t('accounts.organizationInvitations.remove')
                  "
                  :icon="
                    organizationInvitation.status === 'pending' ? 'mdi-close' : 'mdi-delete-outline'
                  "
                  size="small"
                  variant="text"
                  density="comfortable"
                  :disabled="!!deletingOrganizationInvitationId"
                  @click="organizationInvitationToDelete = organizationInvitation"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <v-dialog
        :model-value="!!organizationInvitationToDelete"
        max-width="500"
        :persistent="!!deletingOrganizationInvitationId"
        @update:model-value="organizationInvitationToDelete = undefined"
      >
        <v-card v-if="organizationInvitationToDelete">
          <v-card-title class="flex items-center">
            <span>{{
              organizationInvitationToDelete.status === "pending"
                ? t("accounts.organizationInvitations.cancel")
                : t("accounts.organizationInvitations.remove")
            }}</span>
            <v-spacer />
            <v-btn
              icon="mdi-close"
              :aria-label="t('common.close')"
              variant="text"
              size="small"
              :disabled="!!deletingOrganizationInvitationId"
              @click="organizationInvitationToDelete = undefined"
            />
          </v-card-title>
          <v-card-text>{{
            organizationInvitationToDelete.status === "pending"
              ? t("accounts.organizationInvitations.cancelConfirm", {
                  companyName: organizationInvitationToDelete.companyName,
                })
              : t("accounts.organizationInvitations.removeConfirm", {
                  companyName: organizationInvitationToDelete.companyName,
                })
          }}</v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <v-btn
              :disabled="!!deletingOrganizationInvitationId"
              @click="organizationInvitationToDelete = undefined"
              >{{ t("common.close") }}</v-btn
            >
            <v-spacer />
            <v-btn
              variant="flat"
              color="error"
              :loading="!!deletingOrganizationInvitationId"
              :disabled="!!deletingOrganizationInvitationId"
              data-testid="accounts-organization-invitation-delete-confirm"
              @click="deleteAccountOrganizationInvitation(organizationInvitationToDelete)"
              >{{
                organizationInvitationToDelete.status === "pending"
                  ? t("accounts.organizationInvitations.cancel")
                  : t("accounts.organizationInvitations.remove")
              }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-data-table-virtual
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="groupedAccounts"
        :loading="loadingAccounts"
        :header-props="{ class: 'text-left font-bold' }"
        item-value="rowKey"
        hover
        fixed-header
        class="mt-6 grow"
        style="min-height: 0"
      >
        <template #loading>
          <v-skeleton-loader type="table-tbody@2"></v-skeleton-loader>
        </template>

        <template #item="{ item, props: rowProps, itemRef }">
          <tr
            v-if="item.type === 'group-header'"
            v-bind="rowProps"
            :ref="itemRef"
            style="cursor: pointer"
            @click="toggleGroupGroup(item.groupId)"
          >
            <td colspan="3" class="text-left font-bold pl-0">
              <div class="flex items-center">
                <v-btn
                  icon
                  variant="text"
                  size="x-small"
                  class="mr-2"
                  @click.stop="toggleGroupGroup(item.groupId)"
                >
                  <v-icon>
                    {{
                      expandedGroups.has(item.groupId) ? "mdi-chevron-down" : "mdi-chevron-right"
                    }}
                  </v-icon>
                </v-btn>
                {{ item.groupName }}
              </div>
            </td>
            <td class="text-left font-bold">
              {{ item.totalBalance }}
            </td>
          </tr>

          <RowLink
            v-else
            v-bind="rowProps"
            :ref="itemRef"
            :to="{ name: 'Account', params: { accountId: item.id } }"
          >
            <RowLinkCell focusable :style="item.groupId ? 'padding-left: 62px;' : ''">
              {{ item.name }}
              <v-badge
                v-if="item.pendingTransfersCount && item.pendingTransfersCount > 0"
                v-tooltip="t('accounts.pendingTransfers', { count: item.pendingTransfersCount })"
                style="z-index: 1; position: relative"
                color="warning"
                :content="item.pendingTransfersCount"
                inline
              />
              <UnreadCommentsMark
                :count="item.unreadCommentsCount"
                style="z-index: 1; position: relative"
              />
            </RowLinkCell>
            <RowLinkCell width="160px">
              <AircapLogo
                v-if="getProviderType(item.accountType) === 'proAccount'"
                v-tooltip="t('accounts.accountPro')"
                style="z-index: 1; position: relative"
                size="16px"
              />
              <v-icon
                v-else
                v-tooltip="t('accounts.openBanking') + ' (' + item.accountType + ')'"
                style="z-index: 1"
                size="small"
                >mdi-bank-outline</v-icon
              >
            </RowLinkCell>
            <RowLinkCell width="100px">
              <AccountStatusIcon :account="item" style="z-index: 1" />
            </RowLinkCell>
            <RowLinkCell width="200px">{{ item.balanceFormatted }}</RowLinkCell>
          </RowLink>
        </template>

        <template #no-data>
          <div class="text-center py-3" data-testid="accounts-empty">
            {{ t("accounts.noAccounts") }}
          </div>
        </template>
      </v-data-table-virtual>
      <AddAccountDialog
        v-if="user.hasRole(supervisorRoles, 'supervisorOrg')"
        v-model="showAddAccountDialog"
      ></AddAccountDialog>
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import AccountStatusIcon from "@/components/AccountStatusIcon.vue";
import AircapLogo from "@/components/AircapLogo.vue";
import RowLink from "@/components/RowLink/RowLink.vue";
import RowLinkCell from "@/components/RowLink/RowLinkCell.vue";
import UnreadCommentsMark from "@/components/UnreadCommentsMark.vue";
import { useUnreadCommentsByAccount } from "@/composables/useComments";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { call, db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { formatCurrency } from "@/utils/tools";
import type { Account } from "@aircap/common/account";
import {
  getAccountBalance,
  getAccountState,
  getAccountStatusSortPriority,
  getProviderType,
  type AccountState,
} from "@aircap/common/account";
import {
  accountOrganizationInvitationsFullPath,
  type AccountOrganizationInvitation,
} from "@aircap/common/accountOrganizationInvitation";
import { supervisorRoles } from "@aircap/common/auth";
import { supervisorOrgGroupsFullPath, type SupervisorOrgGroup } from "@aircap/common/supervisorOrg";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import { collection, orderBy, query, where } from "firebase/firestore";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import type { VTextField } from "vuetify/components";
import AddAccountDialog from "./AddAccountDialog.vue";

type WithId<T> = T & { id: string };
type AccountDoc = WithId<Account>;

type DisplayAccount = AccountDoc & {
  balanceFormatted: string;
  balanceNumeric: number;
  currency: string;
  groupName: string | null;
  pendingTransfersCount?: number;
  unreadCommentsCount: number;
  state: AccountState;
};

type GroupHeaderItem = {
  type: "group-header";
  rowKey: string;
  groupName: string;
  groupId: string;
  totalBalance: string;
  totalBalanceNumeric: number | null;
};

type AccountItem = DisplayAccount & { type: "account"; rowKey: string };
type GroupedItem = GroupHeaderItem | AccountItem;

type SortItem = { key: string; order: "asc" | "desc" };

const NO_GROUP_ID = "no-group" as const;

/** Keep Vuetify from reordering mixed group/account rows; we sort in groupedAccounts. */
const preserveItemOrder = () => 0;

const compareAccountsByPendingThenName = (a: DisplayAccount, b: DisplayAccount) => {
  const pendingDiff = (b.pendingTransfersCount ?? 0) - (a.pendingTransfersCount ?? 0);
  if (pendingDiff !== 0) return pendingDiff;
  return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
};

const user = useUserStore();
const { t, locale } = useI18n();
const { showSuccessToast, showErrorToast } = useToast();
const showAddAccountDialog = ref(false);
const activeAccountsFilter = ref<"actived" | "archived">("actived");
const isExternalApprover = computed(
  () => user.hasRole(["supervisor.externalApprover"], "supervisorOrg") && !user.isKind("admin"),
);
const ownerFilter = ref<"mine" | "all">(
  user.isKind("admin") || isExternalApprover.value ? "all" : "mine",
);
const searchQuery = ref("");
const searchInputRef = ref<InstanceType<typeof VTextField> | null>(null);
const expandedGroups = ref<Set<string>>(new Set<string>());
const sortBy = ref<SortItem[]>([]);

const { platform } = useDisplay();

const shortcutLabel = computed(() => {
  return platform.value.mac ? "Cmd+K" : "Ctrl+K";
});

const compareDisplayAccounts = (a: DisplayAccount, b: DisplayAccount) => {
  const current = sortBy.value[0];
  if (!current) {
    return compareAccountsByPendingThenName(a, b);
  }

  const { key, order } = current;
  let result = 0;
  switch (key) {
    case "name":
      result = a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
      break;
    case "accountType":
      result = a.accountType.localeCompare(b.accountType);
      break;
    case "status":
      result = getAccountStatusSortPriority(a) - getAccountStatusSortPriority(b);
      break;
    case "balanceFormatted":
      result = a.balanceNumeric - b.balanceNumeric;
      break;
  }
  if (result === 0) {
    result = compareAccountsByPendingThenName(a, b);
  }
  return order === "desc" ? -result : result;
};

const headers = computed(() => [
  { title: t("common.name"), key: "name", sortable: true, sortRaw: preserveItemOrder },
  {
    title: t("common.type"),
    key: "accountType",
    sortable: true,
    width: "160px",
    sortRaw: preserveItemOrder,
  },
  {
    title: t("common.status"),
    key: "status",
    sortable: true,
    width: "100px",
    sortRaw: preserveItemOrder,
  },
  {
    title: t("common.balance"),
    key: "balanceFormatted",
    sortable: true,
    width: "200px",
    sortRaw: preserveItemOrder,
  },
]);

const accountsSource = computed(() => {
  if (user.isKind("user") && user.uid) {
    return query(
      collection<Account>(db, "accounts"),
      where<Account>("userIds", "array-contains", user.uid),
    );
  }

  if (!user.supervisorOrgId) return null;

  const extraConditions = [];
  if (
    user.hasRole(["supervisor.externalApprover"], "supervisorOrg") &&
    !user.isKind("admin") &&
    user.uid
  ) {
    extraConditions.push(where<Account>("externalApprovers", "array-contains", user.uid));
  }

  return query(
    collection<Account>(db, "accounts"),
    where<Account>("supervisorOrgId", "==", user.supervisorOrgId),
    ...extraConditions,
  );
});

const organizationInvitationsSource = computed(() =>
  user.supervisorOrgId && !user.isKind("user") && !isExternalApprover.value
    ? query(
        collection<AccountOrganizationInvitation>(db, accountOrganizationInvitationsFullPath),
        where<AccountOrganizationInvitation>("supervisorOrgId", "==", user.supervisorOrgId),
        orderBy<AccountOrganizationInvitation>("createdAt", "desc"),
      )
    : null,
);
const { data: organizationInvitations } = useCollection<AccountOrganizationInvitation>(
  organizationInvitationsSource,
);

const organizationInvitationToDelete = ref<AccountOrganizationInvitation & { id: string }>();
const deletingOrganizationInvitationId = ref<string>();
const deleteAccountOrganizationInvitation = async (
  organizationInvitation: AccountOrganizationInvitation & { id: string },
) => {
  if (!user.supervisorOrgId) return;
  deletingOrganizationInvitationId.value = organizationInvitation.id;
  try {
    await call("deleteAccountOrganizationInvitation")({
      supervisorOrgId: user.supervisorOrgId,
      organizationInvitationId: organizationInvitation.id,
    });
    showSuccessToast(
      organizationInvitation.status === "pending"
        ? t("accounts.organizationInvitations.canceled")
        : t("accounts.organizationInvitations.removed"),
    );
    organizationInvitationToDelete.value = undefined;
  } catch (cause) {
    if (cause instanceof FirebaseError && cause.code === "functions/failed-precondition") {
      showErrorToast(t("accounts.organizationInvitations.alreadySigned"));
      organizationInvitationToDelete.value = undefined;
      return;
    }
    const error = new Error(`Error deleting organization invitation ${organizationInvitation.id}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    deletingOrganizationInvitationId.value = undefined;
  }
};

const groupsSource = computed(() => {
  return user.supervisorOrgId
    ? collection<SupervisorOrgGroup>(db, supervisorOrgGroupsFullPath(user.supervisorOrgId))
    : null;
});

const { data: accountsData, pending: loadingAccounts } = useCollection<Account>(accountsSource);
const { data: groupsData } = useCollection<SupervisorOrgGroup>(groupsSource);
const unreadCommentsByAccount = useUnreadCommentsByAccount();

const groupMap = computed<Map<string, string>>(() => {
  const map = new Map<string, string>();
  const groups = groupsData.value ?? [];
  groups.forEach((g) => map.set(g.id, g.name));
  return map;
});

const processedAccounts = computed<DisplayAccount[]>(() => {
  const all = accountsData.value ?? [];
  const filteredByStatus = all.filter((p) =>
    activeAccountsFilter.value === "actived" ? !p.isArchived : p.isArchived,
  );

  const userId = user.uid;

  const filteredAssigned =
    ownerFilter.value === "mine" && userId && !user.isKind("admin")
      ? filteredByStatus.filter((p) =>
          isExternalApprover.value
            ? p.externalApprovers?.includes(userId)
            : p.assignedSupervisors.mode === "selected" &&
              p.assignedSupervisors.uids.includes(userId),
        )
      : filteredByStatus;

  const search = searchQuery.value.trim().toLocaleLowerCase();
  const filtered = search
    ? filteredAssigned.filter((p) => p.name.toLocaleLowerCase().includes(search))
    : filteredAssigned;

  return filtered.map((account) => {
    const { balance, currency = "EUR" } = getAccountBalance(account);

    return {
      ...account,
      id: account.id,
      balanceFormatted:
        balance !== undefined
          ? formatCurrency({ value: balance, currencyCode: currency, locale: locale.value })
          : "",
      balanceNumeric: balance ?? 0,
      currency,
      groupName: account.groupId ? (groupMap.value.get(account.groupId) ?? null) : null,
      pendingTransfersCount: account.pendingTransfersCount ?? 0,
      unreadCommentsCount: unreadCommentsByAccount.value.get(account.id) ?? 0,
      state: getAccountState(account),
    } satisfies DisplayAccount;
  });
});

const hasSetInitialDefault = ref(false);
watch(
  [accountsData, loadingAccounts, accountsSource],
  ([accounts, loading, source]) => {
    if (user.isKind("admin") || isExternalApprover.value) {
      hasSetInitialDefault.value = true;
      return;
    }

    // Wait until the Firestore query is bound and the first snapshot has arrived.
    // A null source (no supervisorOrgId yet) reports pending=false with empty data.
    if (!hasSetInitialDefault.value && !loading && source) {
      const userId = user.uid;

      const hasAssignedActive = userId
        ? accounts.some(
            (p) =>
              !p.isArchived &&
              p.assignedSupervisors.mode === "selected" &&
              p.assignedSupervisors.uids.includes(userId),
          )
        : false;

      if (!hasAssignedActive) {
        ownerFilter.value = "all";
      }
      hasSetInitialDefault.value = true;
    }
  },
  { immediate: true },
);

const groupedAccounts = computed<GroupedItem[]>(() => {
  const groups = processedAccounts.value.reduce<Map<string, DisplayAccount[]>>((acc, account) => {
    const key = account.groupId ?? NO_GROUP_ID;
    const list = acc.get(key);
    if (list) {
      list.push(account);
    } else {
      acc.set(key, [account]);
    }
    return acc;
  }, new Map<string, DisplayAccount[]>());

  const result: GroupedItem[] = [];

  const currentSort = sortBy.value[0];

  const groupedEntries = Array.from(groups.entries())
    .filter(([groupId]) => groupId !== NO_GROUP_ID)
    .map(([groupId, accounts]) => {
      const currencies = [...new Set(accounts.map((p) => p.currency))];
      const hasMixedCurrencies = currencies.length > 1;
      const totalBalanceNumeric = hasMixedCurrencies
        ? null
        : accounts.reduce((sum, p) => sum + (p.balanceNumeric ?? 0), 0);
      return { groupId, accounts, hasMixedCurrencies, totalBalanceNumeric };
    })
    .sort((a, b) => {
      const aName = groupMap.value.get(a.groupId) ?? "";
      const bName = groupMap.value.get(b.groupId) ?? "";
      const nameCompare = aName.toLocaleLowerCase().localeCompare(bName.toLocaleLowerCase());

      if (!currentSort) {
        return nameCompare;
      }

      const { key, order } = currentSort;
      if (key === "balanceFormatted") {
        // Mixed-currency groups show "-" and have no comparable total — keep them last
        if (a.totalBalanceNumeric === null && b.totalBalanceNumeric === null) {
          return nameCompare;
        }
        if (a.totalBalanceNumeric === null) return 1;
        if (b.totalBalanceNumeric === null) return -1;

        const balanceCompare = a.totalBalanceNumeric - b.totalBalanceNumeric;
        if (balanceCompare !== 0) {
          return order === "desc" ? -balanceCompare : balanceCompare;
        }
        return nameCompare;
      }
      if (key === "name") {
        return order === "desc" ? -nameCompare : nameCompare;
      }
      return nameCompare;
    });

  for (const { groupId, accounts, hasMixedCurrencies, totalBalanceNumeric } of groupedEntries) {
    let totalBalance: string;
    if (hasMixedCurrencies || totalBalanceNumeric === null) {
      totalBalance = "-";
    } else {
      const currency = accounts[0]?.currency ?? "EUR";
      totalBalance = formatCurrency({
        value: totalBalanceNumeric,
        currencyCode: currency,
        locale: locale.value,
      });
    }

    result.push({
      type: "group-header",
      rowKey: `group-${groupId}`,
      groupName: groupMap.value.get(groupId) ?? "",
      groupId,
      totalBalance,
      totalBalanceNumeric,
    });

    if (expandedGroups.value.has(groupId)) {
      const sorted = [...accounts].sort(compareDisplayAccounts);
      for (const proj of sorted) {
        result.push({ type: "account", rowKey: `project-${proj.id}`, ...proj });
      }
    }
  }

  if (groups.has(NO_GROUP_ID)) {
    const noGroupAccounts = [...(groups.get(NO_GROUP_ID) ?? [])].sort(compareDisplayAccounts);
    for (const proj of noGroupAccounts) {
      result.push({ type: "account", rowKey: `project-${proj.id}`, ...proj });
    }
  }

  return result;
});

const toggleGroupGroup = (groupId: string) => {
  const next = new Set(expandedGroups.value);
  if (next.has(groupId)) {
    next.delete(groupId);
  } else {
    next.add(groupId);
  }
  expandedGroups.value = next;
};

const handleKeyDown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === "k") {
    event.preventDefault();
    searchInputRef.value?.focus();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
});
</script>

<style scoped>
:deep(.v-field__outline) {
  --v-field-border-opacity: 0.12;
}
</style>
